// src/pages/Dashboard/Noticias.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Button, 
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import StepMenuNoticias from '../../components/StepMenuNoticias';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PlataformaSelector from '../../components/guias/PlataformaSelector';
import ActiveCampaignForm from '../../components/ActiveCampaignForm';

const Noticias = () => {
  const { t } = useTranslation();
  
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pasoActivo, setPasoActivo] = useState('');

  // Referencias para cada noticia
  const newsRefs = useRef([]);

  // Estados para el header (agregar a los estados existentes)
  const [guidesModalOpen, setGuidesModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await api.get('/news');
        setNews(response.data);
        // Inicializar referencias
        newsRefs.current = response.data.map(() => React.createRef());
        if (response.data.length > 0) {
          setPasoActivo(response.data[0].new_id);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Función para scroll al hacer clic en el índice
  const handleScrollTo = (index) => {
    // Cambiar el paso activo al hacer clic
    setPasoActivo(news[index].new_id);
    
    const targetRef = newsRefs.current[index];
    if (targetRef?.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleOpenGuidesModal = () => {
    setGuidesModalOpen(true);
  };

  const handleCloseGuidesModal = () => {
    setGuidesModalOpen(false);
  };

  const handleAddNewDomain = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100vw",
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Cabecera fija */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: "12px 16px", sm: "8px 16px", md: "12px 16px" },
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          backgroundColor: "white",
          zIndex: 1000,
        }}
      >
        {/* Fila principal */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Columna izquierda: Texto */}
          <Box sx={{ 
            display: { xs: "flex", sm: "flex" }, 
            flexDirection: "column", 
            flex: 1,
            ml: { xs: "50px", sm: "50px", md: 35.5 },
          }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "18px", sm: "16px", md: "14px", lg: "20px" },
                fontWeight: "medium",
                color: "#000",
              }}
            >
              Todas las <span style={{ color: "#0F52BD" }}>Noticias</span>
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: "12px", sm: "15px", md: "14px" },
                color: "#666",
                mt: { xs: 0, md: 0.3 },
                display: { xs: "none", sm: "none", md: "none", lg: "block" },
              }}
            >
              Mantente al día con las últimas actualizaciones y novedades del widget.
            </Typography>
          </Box>

          {/* Columna derecha: Botones */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2 },
              mr: { xs: 1.5, md: 5 },
              alignItems: "center",
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "none", md: "none", lg: "flex" }, gap: 0.5 }}>
              <Button
                variant="outlined"
                onClick={handleOpenGuidesModal}
                startIcon={<MenuBookIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                  color: "#0F52BD",
                  borderColor: "#0F52BD",
                  fontSize: { sm: "12px", md: "12px" },
                  px: { sm: 1.5, md: 1.5 },
                  py: { sm: 0.8, md: 0.8 },
                  borderWidth: "2px",
                  borderRadius: "8px",
                }}
              >
                Guía de Implementación
              </Button>
            </Box>
            
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: { sm: "18px", md: "16px" } }} />}
                onClick={handleAddNewDomain}
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                  backgroundColor: "#0F52BD",
                  fontSize: { sm: "12px", md: "12px" },
                  px: { sm: 1.5, md: 1.5 },
                  py: { sm: 0.8, md: 0.8 },
                  borderRadius: "8px",
                }}
              >
                Añadir un nuevo dominio
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Spacer para empujar el contenido hacia abajo */}
      <Box
        sx={{
          height: { xs: "65px", sm: "70px", md: "100px", lg: "90px" },
          "@media (max-width: 600px) and (min-resolution: 1.25dppx)": {
            height: "65px",
          },
          "@media (max-width: 600px) and (min-resolution: 1.5dppx)": {
            height: "90px",
          },
          "@media (min-width: 600px) and (min-resolution: 1.25dppx)": {
            height: "75px",
          },
          "@media (min-width: 900px) and (min-resolution: 1.25dppx)": {
            height: "95px",
          },
        }}
      />

      <Box sx={{ 
        display: "flex", 
        alignItems: "flex-start", 
        ml: { xs: -6, md: 0 },
        minHeight: 'calc(100vh - 200px)',
        position: 'relative'
      }}>
        {/* Índice lateral - MARGEN FIJO */}
        <Box sx={{ 
          display: { xs: 'none', md: 'block' },
          position: 'sticky',
          // CAMBIAR: Usar calc() para mantener distancia fija del header
          top: 'calc(120px + 16px)', // Altura del header + margen deseado
          alignSelf: 'flex-start',
          height: 'calc(60vh - 16px)', // Ajustar altura para compensar el margen extra
          width: '280px',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '10px',
            '&:hover': {
              background: '#a8a8a8',
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#c1c1c1 #f1f1f1',
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          backgroundColor: '#FAFAFA',
          p: 1,
          mr: 3,
          flexShrink: 0,
          // AGREGAR: Media queries para diferentes resoluciones
          '@media (min-resolution: 1.25dppx)': {
            top: 'calc(130px + 16px)', // Altura del header + margen
            height: 'calc(60vh - 16px)'
          },
          '@media (min-resolution: 1.5dppx)': {
            top: 'calc(140px + 16px)',
            height: 'calc(60vh - 16px)'
          },
          '@media (min-resolution: 2dppx)': {
            top: 'calc(150px + 16px)',
            height: 'calc(60vh - 16px)'
          }
        }}>
          <StepMenuNoticias 
            handleScrollTo={handleScrollTo} 
            pasoActivo={pasoActivo} 
            news={news}
            formatDate={formatDate}
          />
        </Box>

        {/* Contenedor de noticias - MANTENER IGUAL */}
        <Box sx={{ 
          display: "flex", 
          flexGrow: 1, 
          paddingLeft: { xs: 0, md: 4 },
          flexDirection: 'column',
          minWidth: 0
        }}>
          <Box sx={{ 
            flexGrow: 1, 
            paddingLeft: 4.26, 
            position: "relative",
            width: '100%'
          }}>
            {/* Resto del contenido sin cambios */}
            <Box
              sx={{
                position: "absolute",
                width: "4px",
                height: "100%",
                backgroundColor: "rgb(162, 182, 212)",
                left: 0,
                top: 10,
                display: { xs: 'none', md: 'block' }
              }}
            />

            {/* Noticias - sin cambios */}
            {news.map((item, index) => (
              <Box key={item.new_id} sx={{ position: "relative", paddingLeft: 0, mb: 6.5 }}>
                <Box
                  sx={{
                    width: "17px",
                    height: "33px",
                    backgroundColor: "#0F52BD",
                    borderRadius: "10px",
                    position: "absolute",
                    left: "-40.6px",
                    top: '0px',
                    display: { xs: 'none', md: 'block' }
                  }}
                />
                
                <Box ml={{ xs: -2, md: 0 }}>
                  <Typography 
                    ref={newsRefs.current[index]} 
                    variant="h5" 
                    sx={{ 
                      color: "#0F52BD", 
                      fontWeight: "medium", 
                      scrollMarginTop: "120px",
                      mb: 1
                    }}
                  >
                    {item.summary}
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: "#000", 
                      fontWeight: "medium", 
                      mb: 2,
                      fontSize: "18px"
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: "#666", 
                      mb: 3,
                      fontSize: "14px"
                    }}
                  >
                    {formatDate(item.publish_date)}
                  </Typography>

                  {/* Descripción */}
                  {item.description && Array.isArray(item.description) && (
                    <Box sx={{ mb: 3 }}>
                      {item.description.map((desc, descIndex) => (
                        <Typography 
                          key={descIndex}
                          variant="body1" 
                          sx={{ 
                            mb: 2,
                            lineHeight: 1.6
                          }}
                        >
                          • {desc}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {/* Información adicional */}
                  {/* <Paper
                    sx={{
                      padding: 2,
                      backgroundColor: "#F8F9FA",
                      border: "1px solid #E0E0E0",
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                      <strong>Tipo de servicio:</strong> {item.service_type}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      <strong>Estado:</strong> {item.status ? 'Activo' : 'Inactivo'}
                    </Typography>
                  </Paper> */}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        
      </Box>

      {/* Modales */}
      <Dialog 
        open={guidesModalOpen} 
        onClose={handleCloseGuidesModal} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            m: 0,
            overflow: 'hidden',
            '& .MuiDialogContent-root': {
              padding: 0,
              margin: 0,
            }
          }
        }}
      >
        <DialogContent sx={{ 
          padding: 0, 
          margin: 0,
          '&:first-of-type': {
            paddingTop: 0
          }
        }}>
          <PlataformaSelector onClose={handleCloseGuidesModal} />
        </DialogContent>
      </Dialog>

      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 'medium', fontSize: '24px' }}>{t("Suscribirse a un Widget")}</DialogTitle>
        <DialogContent sx={{ padding: '25px' }}>
          <ActiveCampaignForm open={modalOpen} />
        </DialogContent>
        <DialogActions sx={{ paddingRight: '25px', paddingBottom: '25px' }}>
          <Button onClick={handleCloseModal} sx={{ textTransform: 'none', fontSize: '16px', fontWeight: 'regular' }}>
            {t("Cerrar")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Noticias;